// CETRA-38: keep legacy effect timing while clearing release ESLint warnings.
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from 'react';
import { Box, CircularProgress } from '@mui/material';
import UserApi from '../../../rest-api/user-api';
import PartnerApi from '../../../rest-api/partner-api';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import { AuthState } from '../../../stores/auth-store';
import DownloadUtils from '../../../utils/download-utils';
import ImageCacheContext from './image-cache-context';
import { notifyAuthExpired, isAuthExpiredError } from '../../../utils/auth-error-utils';

function HandoutImageManager({ handoutData, children }) {
    const { authStore, imageStore } = useContext(StoreContext);
    const [recentlyCachedUrls, setRecentlyCachedUrls] = useState(new Set());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadOrFetchImages();
    }, []);

    function extractImageUrls() {
        const extractedUrls = [];
        handoutData.contentData['en-UK'].forEach((section) => {
            section.blocks.forEach((block) => {
                if (block.type === 'iconStrip') {
                    block.iconDetails.forEach((icon) => extractedUrls.push(icon.img));
                } else if (block.type === 'illustration') {
                    extractedUrls.push(block.src);
                }
            });
        });
        return extractedUrls;
    }

    async function loadOrFetchImages() {
        console.log('loadOrFetchImages called');
        try {
            if (!handoutData) throw new Error('No handout data found');

            const imageUrls = extractImageUrls(handoutData);
            // console.log('Image URLs to process:', imageUrls);
            const idToken = authStore.idToken;
            // console.log('ID Token:', idToken);

            let uncachedUrls = imageUrls;

            if (authStore.authState !== AuthState.IFRAME_AUTHENTICATED) {
                if (imageStore) {
                    // Normal flow with imageStore
                    const imageObjects = await Promise.all(
                        imageUrls.map((url) => imageStore.getImageFromIndexedDB(url)),
                    );
                    uncachedUrls = imageUrls.filter((url, index) => !imageObjects[index]);
                    const successfulUrls = imageUrls.filter((_, index) => imageObjects[index]);
                    setRecentlyCachedUrls(new Set(successfulUrls));
                }
            }

            if (uncachedUrls.length) {
                const payload = {
                    files: uncachedUrls.map((url) => ({ method: 'get', key: url })),
                    contentCategory: 'image',
                };

                let result;

                if (authStore.authState === AuthState.IFRAME_AUTHENTICATED) {
                    // Use PartnerApi to generate presigned URLs for iframe-authenticated flow
                    // console.warn('using iframe generatePresignedUrls');
                    result = await PartnerApi.generatePresignedUrls(idToken, payload.files, 'image');
                } else {
                    // Normal user API flow
                    result = await UserApi.generatePresignedUrls(idToken, payload);
                }

                const presignedUrls = result.files || result.getUrls;

                const downloadResults = await Promise.allSettled(
                    presignedUrls.map((r, index) =>
                        DownloadUtils.downloadImageFromPresignedUrl(r.presignedUrl).catch(async (error) => {
                            // If download fails (e.g., CORS error from expired URL), retry with fresh presigned URL
                            console.warn(
                                `[HandoutImageManager] Image download failed for ${uncachedUrls[index]}, retrying with fresh presigned URL...`,
                                error,
                            );

                            try {
                                // Generate a fresh presigned URL for just this image
                                const retryPayload = {
                                    files: [{ method: 'get', key: uncachedUrls[index] }],
                                    contentCategory: 'image',
                                };

                                const retryResult =
                                    authStore.authState === AuthState.IFRAME_AUTHENTICATED
                                        ? await PartnerApi.generatePresignedUrls(idToken, retryPayload.files, 'image')
                                        : await UserApi.generatePresignedUrls(idToken, retryPayload);

                                const freshPresignedUrl =
                                    (retryResult.files || retryResult.getUrls)[0].presignedUrl;

                                // Retry the download with fresh URL
                                return await DownloadUtils.downloadImageFromPresignedUrl(freshPresignedUrl);
                            } catch (retryError) {
                                console.error(
                                    `[HandoutImageManager] Retry failed for ${uncachedUrls[index]}:`,
                                    retryError,
                                );
                                throw retryError;
                            }
                        }),
                    ),
                );

                const successfulDownloads = downloadResults
                    .filter((res) => res.status === 'fulfilled')
                    .map((res) => res.value);

                if (authStore.authState !== AuthState.IFRAME_AUTHENTICATED && imageStore) {
                    // Save to IndexedDB only for normal flow with imageStore
                    await Promise.all(
                        successfulDownloads.map((downloadResult, index) => {
                            const url = uncachedUrls[index];
                            return imageStore.saveImageToIndexedDB(url, downloadResult.base64data, downloadResult.eTag);
                        }),
                    );
                }

                const successfulUrls = successfulDownloads.map((_, index) => uncachedUrls[index]);
                setRecentlyCachedUrls((prev) => new Set([...prev, ...successfulUrls]));
            }

            setLoading(false);
        } catch (error) {
            console.error('Error in loadOrFetchImages:', error);
            if (isAuthExpiredError(error)) {
                notifyAuthExpired({ source: 'handout-images' });
            }
            setLoading(false);
        }
    }

    const content = loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" width={'100%'} height={118}>
            <CircularProgress />
        </Box>
    ) : (
        <ImageCacheContext.Provider value={recentlyCachedUrls}>{children}</ImageCacheContext.Provider>
    );

    return content;
}

export default HandoutImageManager;
